import React from 'react'
import { RowSelection } from '../../../legacy-design-system/grid/grid'
import { ActionList } from '../../../legacy-design-system/action'
import { Role } from '@contrast-security-inc/base-types'
import { Route } from '../../../graphql/schema/types/route'
import { DeleteByFiltersAction } from './delete/delete-by-filters-action'
import { DeleteByRouteHashesAction } from './delete/delete-by-route-hashes-action'
import {
  ExportByRouteHashesAction,
  ExportByFiltersAction
} from './export/export-actions'
import { ExcludeByRouteHashesAction } from './exclude/exclude-by-route-hashes-action'
import { IncludeByRouteHashesAction } from './include/include-by-route-hashes-action'
import { ExcludeByFiltersAction } from './exclude/exclude-by-filters-action'
import { IncludeByFiltersAction } from './include/include-by-filters-action'
import { routeMutationOnSuccess } from './shared'
import { useQueryClient } from 'react-query'

interface ActionProps {
  readonly applicationRole: Role
  readonly organizationID: string
  readonly applicationID: string
  readonly onSuccess: () => void
}

export function BulkActionList({
  invertedSelection,
  organizationID,
  applicationRole,
  applicationID,
  selectedItems,
  deselectedItems,
  onSuccess,
  filters,
  startDate,
  endDate,
  discoveredStartDate,
  discoveredEndDate
}: RowSelection<Route, any> &
  ActionProps & {
    readonly startDate: Date | undefined
    readonly endDate: Date | undefined
    readonly discoveredStartDate: Date | undefined
    readonly discoveredEndDate: Date | undefined
  }) {
  const queryClient = useQueryClient()

  function mutationOnSuccess() {
    return routeMutationOnSuccess({ queryClient, onSuccess })
  }

  if (invertedSelection) {
    // Build filters object, combining common grid filters and custom filters
    const transformedFilters = buildFilters({
      filters,
      startDate,
      endDate,
      discoveredStartDate,
      discoveredEndDate
    })

    return (
      <ActionList large>
        {filters.quickFilter !== 'EXCLUDED' ? (
          <ExcludeByFiltersAction
            applicationRole={applicationRole}
            organizationUuid={organizationID}
            applicationUuid={applicationID}
            onSuccess={mutationOnSuccess}
            excludedHashes={deselectedItems.map(({ id }) => id)}
            filters={transformedFilters}
          />
        ) : (
          <IncludeByFiltersAction
            applicationRole={applicationRole}
            organizationUuid={organizationID}
            applicationUuid={applicationID}
            onSuccess={mutationOnSuccess}
            excludedHashes={deselectedItems.map(({ id }) => id)}
            filters={transformedFilters}
          />
        )}
        <DeleteByFiltersAction
          applicationRole={applicationRole}
          organizationUuid={organizationID}
          applicationUuid={applicationID}
          onSuccess={mutationOnSuccess}
          excludedHashes={deselectedItems.map(({ id }) => id)}
          filters={transformedFilters}
        />
        <ExportByFiltersAction
          organizationUuid={organizationID}
          applicationUuid={applicationID}
          filters={transformedFilters}
          excludedHashes={deselectedItems.map(({ id }) => id)}
        />
      </ActionList>
    )
  }

  return (
    <ActionList large>
      {filters.quickFilter !== 'EXCLUDED' ? (
        <ExcludeByRouteHashesAction
          applicationRole={applicationRole}
          organizationUuid={organizationID}
          applicationUuid={applicationID}
          routeHashes={selectedItems.map(({ id }) => id)}
          onSuccess={mutationOnSuccess}
          pendoId="exclude_bulk_routes"
        />
      ) : (
        <IncludeByRouteHashesAction
          applicationRole={applicationRole}
          organizationUuid={organizationID}
          applicationUuid={applicationID}
          routeHashes={selectedItems.map(({ id }) => id)}
          onSuccess={mutationOnSuccess}
        />
      )}
      <DeleteByRouteHashesAction
        applicationRole={applicationRole}
        organizationUuid={organizationID}
        applicationUuid={applicationID}
        routeHashes={selectedItems.map(({ id }) => id)}
        onSuccess={mutationOnSuccess}
      />
      <ExportByRouteHashesAction
        organizationUuid={organizationID}
        applicationUuid={applicationID}
        routeHashes={selectedItems.map(({ id }) => id)}
        filters={buildFilters({
          filters,
          startDate,
          endDate,
          discoveredStartDate,
          discoveredEndDate
        })}
      />
    </ActionList>
  )
}

export interface BuildFiltersFnProps {
  readonly filters: RowSelection<Route, any>['filters']
  readonly startDate?: Date
  readonly endDate?: Date
  readonly discoveredStartDate?: Date
  readonly discoveredEndDate?: Date
}

export function buildFilters({
  filters: { filterText, quickFilter, aggregations },
  startDate,
  endDate,
  discoveredStartDate,
  discoveredEndDate
}: BuildFiltersFnProps) {
  // @ts-ignore this is typed as any
  const { agentSessionId, ...restAggregations } = aggregations

  return Object.assign(
    {},
    {
      filterText,
      quickFilter,
      startDate:
        typeof startDate === 'undefined' ? undefined : startDate.getTime(),
      endDate: typeof endDate === 'undefined' ? undefined : endDate.getTime(),
      discoveredStartDate: discoveredStartDate?.getTime(),
      discoveredEndDate: discoveredEndDate?.getTime(),
      agentSessionId: agentSessionId?.[0]
    },
    restAggregations
  )
}
